// `standard` and `pro` are retained as internal billing values so existing
// rows, Stripe webhooks, and admin tooling remain compatible. They are not
// Flagship entitlements. General is available to every active account.
export type Tier = "standard" | "pro";

export type MembershipInput = {
  tier: string | null | undefined;
  expiresAt: Date | string | number | null | undefined;
};

export type MembershipStatus = "free" | "trial" | "subscribed" | "expired";

function expiryDate(
  value: MembershipInput["expiresAt"],
): Date | null | "invalid" {
  if (value === null || value === undefined) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? "invalid" : parsed;
}

export function hasActiveAccess(
  input: MembershipInput,
  now: Date = new Date(),
): boolean {
  const expiry = expiryDate(input.expiresAt);
  // Free General accounts have no Flagship expiry. Existing trials/rewards
  // retain their finite access; permanent Flagship grants use pro with no expiry.
  if (expiry === null) return input.tier === "pro";
  if (expiry === "invalid") return false;
  return expiry.getTime() > now.getTime();
}

export function membershipStatus(
  input: MembershipInput,
  now: Date = new Date(),
): MembershipStatus {
  if (input.tier === "standard" && input.expiresAt == null) return "free";
  if (!hasActiveAccess(input, now)) return "expired";
  return input.tier === "pro" ? "subscribed" : "trial";
}

export function isSubscribed(input: MembershipInput, now?: Date): boolean {
  return membershipStatus(input, now) === "subscribed";
}

// Permanent access is an admin-only grant. Public surfaces use this only to
// suppress checkout controls; they never describe the account as "Lifetime".
export function hasPermanentAccess(input: MembershipInput): boolean {
  return input.tier === "pro" && input.expiresAt == null;
}
